import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Generated from the gdrive image's endpoint table (agentic-gateway
// `docker/gdrive`, `yarn generate:catalog`); regenerate rather than edit. A
// module rather than the JSON itself because the frontend's esbuild cannot
// parse a JSON import attribute.
export const googleDriveAiMcpTools: McpTool[] = [
  {
    name: "list_files",
    description:
      "List files and folders. Scope the query with `q` — for example \"'<folderId>' in parents and trashed = false\" to list a folder's children. Only items the grant reaches are visible; everything else is absent rather than forbidden.",
    inputSchema: {
      type: "object",
      properties: {
        pageSize: {
          type: "integer",
          description: "Maximum results per page",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        q: {
          type: "string",
          description:
            "Drive query string, e.g. \"'FOLDER_ID' in parents and mimeType != 'application/vnd.google-apps.folder'\"",
        },
        orderBy: {
          type: "string",
          description:
            "Comma-separated sort keys, e.g. 'modifiedTime desc,name'",
        },
        driveId: {
          type: "string",
          description:
            "Shared drive to search. Requires corpora='drive'; a grantee with no drive membership should usually omit both and scope with `q` instead.",
        },
        corpora: {
          type: "string",
          description: "Bodies of items to query",
          enum: ["user", "drive", "allDrives"],
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. files(id,name,mimeType,parents,modifiedTime),nextPageToken). Defaults to the API's own default set when omitted.",
        },
      },
      required: [],
    },
  },
  {
    name: "get_file",
    description: "Get a file or folder's metadata.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,name,mimeType,parents,driveId,size). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "create_file",
    description:
      "Create a file or folder from metadata alone (no content). Body is a Files resource: { name, mimeType, parents: [folderId] }. Use mimeType 'application/vnd.google-apps.folder' for a folder. `parents` must name a folder the grant reaches.",
    inputSchema: {
      type: "object",
      properties: {
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,name,mimeType,parents). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["body"],
    },
  },
  {
    name: "update_file",
    description:
      "Update a file or folder's metadata. Body is a partial Files resource, e.g. { name: 'new name' }.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        addParents: {
          type: "string",
          description: "Comma-separated parent IDs to add",
        },
        removeParents: {
          type: "string",
          description: "Comma-separated parent IDs to remove",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,name,parents). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "copy_file",
    description:
      "Copy a file. Body is a partial Files resource for the copy, e.g. { name, parents: [folderId] }.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,name,parents). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "delete_file",
    description:
      "Permanently delete a file or folder and its descendants. A `writer` grant cannot delete the granted folder itself.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "download_file",
    description:
      "Download a binary file's content. For Google-native documents (Docs, Sheets, Slides) use export_file instead.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "export_file",
    description:
      "Export a Google-native document to another format, e.g. mimeType 'text/plain' or 'application/pdf'.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        mimeType: {
          type: "string",
          description: "Target MIME type, e.g. 'text/plain', 'text/markdown'",
        },
      },
      required: ["fileId", "mimeType"],
    },
  },
  {
    name: "upload_file_content",
    description:
      "Replace an existing file's content. Pass the new content as `content` (text) with its `contentType`.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,name,size). Defaults to the API's own default set when omitted.",
        },
        content: {
          type: "string",
          description: "File content to upload",
        },
        contentType: {
          type: "string",
          description: "MIME type of `content` (default text/plain)",
        },
      },
      required: ["fileId", "content"],
    },
  },
  {
    name: "list_revisions",
    description: "List a file's revisions.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        pageSize: {
          type: "integer",
          description: "Maximum results per page",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. revisions(id,modifiedTime,size)). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "get_revision",
    description: "Get one revision of a file.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        revisionId: {
          type: "string",
          description: "Revision ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,modifiedTime,lastModifyingUser). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId", "revisionId"],
    },
  },
  {
    name: "list_comments",
    description:
      "List a file's comments. Drive requires `fields` on this call; it defaults to a full selection when omitted.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        pageSize: {
          type: "integer",
          description: "Maximum results per page",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        includeDeleted: {
          type: "boolean",
          description: "Include deleted comments",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. comments(id,content,author,resolved,replies)). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "get_comment",
    description: "Get one comment on a file.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,content,author,resolved). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId", "commentId"],
    },
  },
  {
    name: "create_comment",
    description: "Comment on a file. Body: { content: string }.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,content,author). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["fileId", "body"],
    },
  },
  {
    name: "update_comment",
    description:
      "Update a comment. Body: { content?: string, resolved?: boolean }.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,content,resolved). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["fileId", "commentId", "body"],
    },
  },
  {
    name: "delete_comment",
    description: "Delete a comment.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
      },
      required: ["fileId", "commentId"],
    },
  },
  {
    name: "list_replies",
    description: "List replies to a comment.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
        pageSize: {
          type: "integer",
          description: "Maximum results per page",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. replies(id,content,author)). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId", "commentId"],
    },
  },
  {
    name: "create_reply",
    description: "Reply to a comment. Body: { content: string }.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,content,author). Defaults to the API's own default set when omitted.",
        },
        body: {
          type: "object",
          description:
            "JSON request body sent as-is to the Drive API; see the tool description for its expected shape",
          additionalProperties: true,
        },
      },
      required: ["fileId", "commentId", "body"],
    },
  },
  {
    name: "delete_reply",
    description: "Delete a reply.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        commentId: {
          type: "string",
          description: "Comment ID",
        },
        replyId: {
          type: "string",
          description: "Reply ID",
        },
      },
      required: ["fileId", "commentId", "replyId"],
    },
  },
  {
    name: "list_file_permissions",
    description:
      "List a file or folder's permissions. Read only — this server cannot grant or revoke access.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        pageSize: {
          type: "integer",
          description: "Maximum results per page",
        },
        pageToken: {
          type: "string",
          description: "Page token from a previous response",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. permissions(id,type,role,emailAddress)). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId"],
    },
  },
  {
    name: "get_file_permission",
    description: "Get one permission on a file or folder.",
    inputSchema: {
      type: "object",
      properties: {
        fileId: {
          type: "string",
          description: "Drive file or folder ID",
        },
        permissionId: {
          type: "string",
          description: "Permission ID",
        },
        fields: {
          type: "string",
          description:
            "Partial-response field selector (e.g. id,type,role,emailAddress). Defaults to the API's own default set when omitted.",
        },
      },
      required: ["fileId", "permissionId"],
    },
  },
  {
    name: "get_about",
    description:
      "Information about the calling identity and its Drive settings. Drive requires `fields` on this call.",
    inputSchema: {
      type: "object",
      properties: {
        fields: {
          type: "string",
          description:
            "Field selector, e.g. 'user,storageQuota' or '*'. Required by the API.",
        },
      },
      required: ["fields"],
    },
  },
];
